/**
 * Panel Configuration Store
 * Defines the navigation structure and panels available in the admin dashboard
 */

import { Panel, PanelType } from '../types/models';

// Re-export PanelType for convenience
export type { PanelType };

export const panels: Record<PanelType, Panel> = {
  master: {
    name: 'Master Admin',
    role: 'Super Admin',
    nav: [
      { id: 'dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'Users' },
      { id: 'users-all', icon: '👥', label: 'All Users' },
      { id: 'users-verified', icon: '✅', label: 'Verified' },
      { id: 'users-pending', icon: '⏳', label: 'Pending KYC' },
      { id: 'users-banned', icon: '🚫', label: 'Banned / Frozen' },
      { type: 'label', label: 'Predictions' },
      { id: 'predictions-overview', icon: '🎯', label: 'Predictions Overview' },
      { id: 'live-betting', icon: '📡', label: 'versusLive' },
      { id: 'hedge-risk', icon: '🛡️', label: 'Hedge & Risk' },
      { id: 'create-market', icon: '➕', label: 'Create Market' },
      { id: 'pending-approval', icon: '⏳', label: 'Pending Approval' },
      { id: 'market-proposals', icon: '📝', label: 'Market Proposals' },
      { type: 'label', label: 'Analytics' },
      { id: 'analytics', icon: '📈', label: 'Analytics' },
      { type: 'label', label: 'Finance' },
      { id: 'payout-queue', icon: '💸', label: 'Payout Queue' },
      { id: 'withdrawals', icon: '📋', label: 'Withdrawals' },
      { id: 'share-earn', icon: '🤝', label: 'Share & Earn' },
      { type: 'label', label: 'Versus Content' },
      { id: 'originals', icon: '📰', label: 'Originals' },
      { id: 'awards', icon: '🏆', label: 'Awards' },
      { id: 'academy', icon: '🎓', label: 'Academy' },
      { id: 'challenges', icon: '⚔️', label: 'Challenges' },
      { type: 'label', label: 'Engagement' },
      { id: 'notifications', icon: '🔔', label: 'Push Notifications' },
      { type: 'label', label: 'Vouchers & Fees' },
      { id: 'vouchers', icon: '🎟️', label: 'Vouchers' },
      { id: 'wallet-credit-vouchers', icon: '💰', label: 'Wallet Credit' },
      { id: 'affiliate-partners', icon: '🤝', label: 'Affiliate Partners' },
      { id: 'platform-fee-config', icon: '💸', label: 'Platform Fee' },
      { type: 'label', label: 'Administration' },
      { id: 'admin-accounts', icon: '🔐', label: 'Admin Accounts' },
      { id: 'system-settings', icon: '⚙️', label: 'System Settings' },
      { id: 'remote-config', icon: '🎛️', label: 'Remote Config' },
      { id: 'audit-log', icon: '📜', label: 'Audit Log' },
      { type: 'label', label: 'Assistant' },
      { id: 'versus-ai', icon: '🧠', label: 'AI Soul & Notes' },
      { id: 'versus-expert', icon: '🤖', label: 'Versus Expert' },
    ],
  },
  risk: {
    name: 'Risk & AML',
    role: 'Risk Analyst',
    nav: [
      { id: 'risk-dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'Fraud' },
      { id: 'risk-cases', icon: '🚨', label: 'Risk Cases' },
      { id: 'network-graph', icon: '🕸️', label: 'Network Analysis' },
      { type: 'label', label: 'AML' },
      { id: 'aml-queue', icon: '💰', label: 'AML Review' },
    ],
  },
  finance: {
    name: 'Finance',
    role: 'Finance Admin',
    nav: [
      { id: 'finance-dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'Payments' },
      { id: 'payout-queue', icon: '💸', label: 'Payout Queue' },
      { id: 'withdrawals', icon: '📋', label: 'Withdrawals' },
      { id: 'share-earn', icon: '🤝', label: 'Share & Earn' },
      { id: 'deposits', icon: '📥', label: 'Deposits' },
    ],
  },
  support: {
    name: 'Support',
    role: 'Support Agent',
    nav: [
      { id: 'support-dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'Tickets' },
      { id: 'tickets-all', icon: '🎫', label: 'All Tickets' },
      { id: 'tickets-mine', icon: '👤', label: 'My Tickets' },
      { id: 'tickets-escalated', icon: '⚠️', label: 'Escalated' },
      { type: 'label', label: 'Tools' },
      { id: 'user-lookup', icon: '🔍', label: 'User Lookup' },
      { type: 'label', label: 'Templates' },
      { id: 'canned-responses', icon: '📝', label: 'Canned Responses' },
      { type: 'label', label: 'Assistant' },
      { id: 'versus-ai', icon: '🧠', label: 'AI Soul & Notes' },
      { id: 'versus-expert', icon: '🤖', label: 'Versus Expert' },
    ],
  },
  compliance: {
    name: 'Compliance',
    role: 'Compliance Officer',
    nav: [
      { id: 'compliance-dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'KYC' },
      { id: 'kyc-queue', icon: '📝', label: 'KYC Queue' },
    ],
  },
  content: {
    name: 'Content',
    role: 'Content Manager',
    nav: [
      { id: 'content-dashboard', icon: '📊', label: 'Dashboard' },
      { type: 'label', label: 'Markets' },
      { id: 'predictions-overview', icon: '🎯', label: 'Predictions Overview' },
      { id: 'create-market', icon: '➕', label: 'Create Market' },
      { id: 'pending-approval', icon: '⏳', label: 'Pending Approval' },
      { id: 'market-proposals', icon: '📝', label: 'Market Proposals' },
      { type: 'label', label: 'Moderation' },
      { id: 'content-reports', icon: '🚩', label: 'Content Reports' },
      { id: 'challenges', icon: '⚔️', label: 'Challenges' },
      { type: 'label', label: 'Display Order' },
      { id: 'bet-ordering', icon: '📋', label: 'Bet Ordering' },
      { type: 'label', label: 'Versus Content' },
      { id: 'originals', icon: '📰', label: 'Originals' },
      { id: 'awards', icon: '🏆', label: 'Awards' },
      { id: 'academy', icon: '🎓', label: 'Academy' },
      { type: 'label', label: 'Engagement' },
      { id: 'notifications', icon: '🔔', label: 'Push Notifications' },
      { id: 'campaigns', icon: '📣', label: 'Campaigns' },
    ],
  },
};

